/**
 * Zod schema for the Edit Profile dialog. Validates the raw `ProfileFormValues`
 * strings — payload construction (the three-state omit/null/value resolution the backend's
 * `UpdateMyMemberInfoInput` expects) happens separately in `useUpdateMyProfile.ts`, since that's
 * a function of both the current and the initial form values, not a property of a single
 * snapshot a Zod schema validates.
 *
 * Bounds mirror the live backend schema (`update-my-member-info.model.ts` on `develop`) exactly,
 * not the design handoff's — the design's per-field caps are uniform placeholders, and accepting
 * more than the backend allows just trades an inline error here for a generic save failure there.
 */
import { isValidUsZip, US_ZIP_REGEX } from '@mdvip/shared/lib/us-zip';
import { z } from 'zod';
import { stripPhoneFormatting } from './profile.format';
import type { ProfileFormValues } from './profile.types';
import { US_STATE_CODES } from './usStates';

export const VALIDATION_KEYS = {
  dateOfBirthRequired: 'profile.validation.dateOfBirthRequired',
  genderRequired: 'profile.validation.genderRequired',
  emailRequired: 'profile.validation.emailRequired',
  emailInvalid: 'profile.validation.emailInvalid',
  phoneRequired: 'profile.validation.phoneRequired',
  phoneInvalid: 'profile.validation.phoneInvalid',
  mobilePhoneInvalid: 'profile.validation.mobilePhoneInvalid',
  mailingStreetRequired: 'profile.validation.mailingStreetRequired',
  mailingStreetTooLong: 'profile.validation.mailingStreetTooLong',
  mailingCityRequired: 'profile.validation.mailingCityRequired',
  mailingCityTooLong: 'profile.validation.mailingCityTooLong',
  mailingStateRequired: 'profile.validation.mailingStateRequired',
  mailingPostalCodeRequired: 'profile.validation.mailingPostalCodeRequired',
  mailingPostalCodeInvalid: 'profile.validation.mailingPostalCodeInvalid',
  mailingPostalCodeOutOfRange: 'profile.validation.mailingPostalCodeOutOfRange',
  otherStreetTooLong: 'profile.validation.otherStreetTooLong',
  otherCityTooLong: 'profile.validation.otherCityTooLong',
  otherPostalCodeTooLong: 'profile.validation.otherPostalCodeTooLong',
  otherAddressIncomplete: 'profile.validation.otherAddressIncomplete',
  primaryInsuranceTooLong: 'profile.validation.primaryInsuranceTooLong',
  secondaryInsuranceTooLong: 'profile.validation.secondaryInsuranceTooLong',
  employerTooLong: 'profile.validation.employerTooLong',
  jobTitleTooLong: 'profile.validation.jobTitleTooLong',
} as const;

// `updateMyMemberInfo`'s own per-line cap (`update-my-member-info.model.ts`'s `MAX_ADDRESS_LINE`),
// applied to each real line independently, not the flattened `mailingStreet`/`otherStreet` field.
// The too-long checks below only run once the street field is touched: untouched, the real
// `line1`/`line2` pair travels verbatim (see `OriginalAddressLines`) and was already valid when
// saved; touched, it collapses to one line on submit, so its length is exactly `line1`'s cap.
const MAX_ADDRESS_LINE = 100;
const MAX_ADDRESS_CITY = 40;
const MAX_ADDRESS_POSTAL_CODE = 20;
const MAX_PROFILE_TEXT = 128;

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

type AddressTouchFields = Pick<
  ProfileFormValues,
  'mailingStreet' | 'otherStreet' | 'otherCity' | 'otherState' | 'otherPostalCode'
>;
const SECONDARY_ADDRESS_KEYS = ['otherStreet', 'otherCity', 'otherState', 'otherPostalCode'] as const;

/**
 * Builds the Edit Profile schema against the dialog's seeded `initial` values. The
 * secondary-address all-or-nothing rule needs `initial` to tell "the member is actively editing
 * this group" apart from "this group already looked like this when the dialog opened," and the
 * two street fields' too-long checks need it for the same reason (see `MAX_ADDRESS_LINE`). Every
 * other field's validation is independent of `initial`.
 */
export function createProfileSchema(initial: AddressTouchFields) {
  return z
    .object({
      dateOfBirth: z.string(),
      gender: z.enum(['MALE', 'FEMALE', '']),
      email: z.string(),
      phone: z.string(),
      mailingStreet: z.string(),
      mailingCity: z.string(),
      mailingState: z.enum([...US_STATE_CODES, '']),
      mailingPostalCode: z.string(),
      salutation: z.enum(['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', '']),
      mobilePhone: z.string(),
      otherStreet: z.string(),
      otherCity: z.string(),
      otherState: z.enum([...US_STATE_CODES, '']),
      otherPostalCode: z.string(),
      primaryInsurance: z.string(),
      secondaryInsurance: z.string(),
      employer: z.string(),
      jobTitle: z.string(),
      preferredContactMethod: z.enum(['EMAIL', 'MOBILE', 'MAIL', 'PHONE', 'WORK_PHONE', '']),
    })
    .superRefine((values, ctx) => {
      // ── Required fields ──────────────────────────────
      if (values.dateOfBirth.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['dateOfBirth'], message: VALIDATION_KEYS.dateOfBirthRequired });
      } else if (!ISO_DATE_REGEX.test(values.dateOfBirth)) {
        // Only reachable via a malformed native date-input value (e.g. devtools tampering) —
        // the browser itself never emits a non-ISO value for `type="date"`.
        ctx.addIssue({ code: 'custom', path: ['dateOfBirth'], message: VALIDATION_KEYS.dateOfBirthRequired });
      }

      if (values.gender === '') {
        ctx.addIssue({ code: 'custom', path: ['gender'], message: VALIDATION_KEYS.genderRequired });
      }

      if (values.email.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['email'], message: VALIDATION_KEYS.emailRequired });
        // Validated against the trimmed value — the payload sends `values.email.trim()`, so
        // validation must agree on what's actually being submitted or a trailing/leading space
        // (common from copy/paste) fails format validation for a value that would save just fine.
      } else if (!z.string().email().safeParse(values.email.trim()).success) {
        ctx.addIssue({ code: 'custom', path: ['email'], message: VALIDATION_KEYS.emailInvalid });
      }

      const phoneDigits = stripPhoneFormatting(values.phone);
      if (phoneDigits.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['phone'], message: VALIDATION_KEYS.phoneRequired });
      } else if (phoneDigits.length !== 10) {
        ctx.addIssue({ code: 'custom', path: ['phone'], message: VALIDATION_KEYS.phoneInvalid });
      }

      if (values.mailingStreet.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['mailingStreet'], message: VALIDATION_KEYS.mailingStreetRequired });
        // Length checks below compare the *trimmed* value — `buildUpdatePayload` trims before
        // submit, so validating the untrimmed length could reject a value the backend would accept.
        // Gated on touched: see `MAX_ADDRESS_LINE`'s own comment for why an untouched field's
        // flattened length doesn't need checking at all.
      } else if (
        values.mailingStreet.trim() !== initial.mailingStreet.trim() &&
        values.mailingStreet.trim().length > MAX_ADDRESS_LINE
      ) {
        ctx.addIssue({ code: 'custom', path: ['mailingStreet'], message: VALIDATION_KEYS.mailingStreetTooLong });
      }

      if (values.mailingCity.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['mailingCity'], message: VALIDATION_KEYS.mailingCityRequired });
      } else if (values.mailingCity.trim().length > MAX_ADDRESS_CITY) {
        ctx.addIssue({ code: 'custom', path: ['mailingCity'], message: VALIDATION_KEYS.mailingCityTooLong });
      }

      if (values.mailingState === '') {
        ctx.addIssue({ code: 'custom', path: ['mailingState'], message: VALIDATION_KEYS.mailingStateRequired });
      }

      if (values.mailingPostalCode.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['mailingPostalCode'],
          message: VALIDATION_KEYS.mailingPostalCodeRequired,
        });
      } else if (!US_ZIP_REGEX.test(values.mailingPostalCode.trim())) {
        ctx.addIssue({
          code: 'custom',
          path: ['mailingPostalCode'],
          message: VALIDATION_KEYS.mailingPostalCodeInvalid,
        });
      } else if (!isValidUsZip(values.mailingPostalCode.trim())) {
        ctx.addIssue({
          code: 'custom',
          path: ['mailingPostalCode'],
          message: VALIDATION_KEYS.mailingPostalCodeOutOfRange,
        });
      }

      // ── Optional fields — validated only when non-blank ─
      const mobileDigits = stripPhoneFormatting(values.mobilePhone);
      if (mobileDigits.length > 0 && mobileDigits.length !== 10) {
        ctx.addIssue({ code: 'custom', path: ['mobilePhone'], message: VALIDATION_KEYS.mobilePhoneInvalid });
      }

      if (values.primaryInsurance.trim().length > MAX_PROFILE_TEXT) {
        ctx.addIssue({ code: 'custom', path: ['primaryInsurance'], message: VALIDATION_KEYS.primaryInsuranceTooLong });
      }
      if (values.secondaryInsurance.trim().length > MAX_PROFILE_TEXT) {
        ctx.addIssue({
          code: 'custom',
          path: ['secondaryInsurance'],
          message: VALIDATION_KEYS.secondaryInsuranceTooLong,
        });
      }
      if (values.employer.trim().length > MAX_PROFILE_TEXT) {
        ctx.addIssue({ code: 'custom', path: ['employer'], message: VALIDATION_KEYS.employerTooLong });
      }
      if (values.jobTitle.trim().length > MAX_PROFILE_TEXT) {
        ctx.addIssue({ code: 'custom', path: ['jobTitle'], message: VALIDATION_KEYS.jobTitleTooLong });
      }

      // ── Secondary address — all four filled, or all four blank, never a mix ─
      const otherAddressFields = SECONDARY_ADDRESS_KEYS.map((key) => values[key]);
      const filledCount = otherAddressFields.filter((field) => field.trim() !== '').length;
      const incomplete = filledCount !== 0 && filledCount !== otherAddressFields.length;
      // A member whose Salesforce record already has a partial secondary address (messy legacy
      // data) shouldn't be locked out of saving an unrelated field forever — only enforce once
      // they've actually started editing this group themselves.
      const touchedSecondaryAddress = SECONDARY_ADDRESS_KEYS.some((key) => values[key].trim() !== initial[key].trim());
      if (incomplete && touchedSecondaryAddress) {
        // NOT `path: ['root']`: RHF's `handleSubmit` unconditionally strips a `root`-path error
        // (it's reserved for a post-submit `setError`, not a resolver-produced one), so a schema
        // issue there never blocks submission — verified live, it instead crashes downstream on
        // `values.email.trim()` with no visible error at all, since `zodResolver` resolves with an
        // empty `values: {}` whenever zod's own parse throws. Landing on the four real field paths
        // uses RHF's normal, non-strippable gating instead.
        for (const field of SECONDARY_ADDRESS_KEYS) {
          ctx.addIssue({ code: 'custom', path: [field], message: VALIDATION_KEYS.otherAddressIncomplete });
        }
      } else if (filledCount === otherAddressFields.length) {
        // Gated on touched, same reasoning as `mailingStreet`'s check above.
        if (
          values.otherStreet.trim() !== initial.otherStreet.trim() &&
          values.otherStreet.trim().length > MAX_ADDRESS_LINE
        ) {
          ctx.addIssue({ code: 'custom', path: ['otherStreet'], message: VALIDATION_KEYS.otherStreetTooLong });
        }
        if (values.otherCity.trim().length > MAX_ADDRESS_CITY) {
          ctx.addIssue({ code: 'custom', path: ['otherCity'], message: VALIDATION_KEYS.otherCityTooLong });
        }
        if (values.otherPostalCode.trim().length > MAX_ADDRESS_POSTAL_CODE) {
          ctx.addIssue({ code: 'custom', path: ['otherPostalCode'], message: VALIDATION_KEYS.otherPostalCodeTooLong });
        }
      }
    });
}

export type ProfileSchemaValues = z.infer<ReturnType<typeof createProfileSchema>>;
